import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api";
import { useAuth } from "../Auth/AuthContext";
import { Navigate } from "react-router-dom";
import { FaBuilding, FaFilePdf, FaEnvelope, FaListUl, FaTable, FaColumns, FaPercent, FaCreditCard, FaUserTie, FaPlug, FaBell } from "react-icons/fa";
import CompanyTab        from "./CompanyTab";
import IntegrationsTab   from "./IntegrationsTab";
import NotificationsTab  from "./NotificationsTab";
import PdfTab            from "./PdfTab";
import EmailTab          from "./EmailTab";
import PrestationTypesTab from "./PrestationTypesTab";
import PaymentMethodsTab from "./PaymentMethodsTab";
import TeamLeadersTab    from "./TeamLeadersTab";
import PresetLinesTab    from "./PresetLinesTab";
import TiersTab          from "./TiersTab";
import CommissionsTab    from "./CommissionsTab";
import { isOrgAdmin } from "../../utils/roles";

const TABS = [
  { id: "company",     tkey: "company",     icon: <FaBuilding /> },
  { id: "pdf",         tkey: "pdf",         icon: <FaFilePdf /> },
  { id: "email",       tkey: "email",       icon: <FaEnvelope /> },
  { id: "prestations", tkey: "prestations", icon: <FaListUl /> },
  { id: "payment",     tkey: "payment",     icon: <FaCreditCard /> },
  { id: "teamLeaders", tkey: "teamLeaders", icon: <FaUserTie /> },
  { id: "presets",     tkey: "presets",     icon: <FaTable /> },
  { id: "tiers",       tkey: "tiers",       icon: <FaColumns /> },
  { id: "commissions", tkey: "commissions", icon: <FaPercent /> },
  { id: "notifications", tkey: "notifications", icon: <FaBell /> },
  { id: "integrations", tkey: "integrations", icon: <FaPlug /> },
];

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("company");
  const [raw, setRaw]             = useState(null);
  const [loading, setLoading]     = useState(true);

  const fetchRaw = async () => {
    try {
      const { data } = await api.get("/settings");
      setRaw(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRaw(); }, []);

  if (!isOrgAdmin(user)) return <Navigate to="/" replace />;

  if (loading) return (
    <div className="flex items-center justify-center h-48">
      <span className="loading loading-spinner loading-lg text-primary" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-base-content">{t("settings.title")}</h1>
        <p className="text-sm text-base-content/50 mt-0.5">{t("settings.subtitle")}</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-0.5 flex-wrap border-b border-base-200">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all duration-150 rounded-t-lg ${
                isActive
                  ? "text-primary"
                  : "text-base-content/45 hover:text-base-content hover:bg-base-200/60"
              }`}
            >
              <span className={`transition-transform duration-150 ${isActive ? "scale-110" : ""}`}>
                {tab.icon}
              </span>
              {t(`settings.tabs.${tab.tkey}`)}
              {isActive && (
                <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-t-full animate-fade-in" />
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div>
        {raw && activeTab === "company"     && <CompanyTab        raw={raw} onSaved={fetchRaw} />}
        {raw && activeTab === "pdf"         && <PdfTab            raw={raw} onSaved={fetchRaw} />}
        {raw && activeTab === "email"       && <EmailTab          raw={raw} />}
        {raw && activeTab === "prestations" && <PrestationTypesTab raw={raw} />}
        {raw && activeTab === "payment"     && <PaymentMethodsTab raw={raw} />}
        {raw && activeTab === "teamLeaders" && <TeamLeadersTab    raw={raw} />}
        {       activeTab === "presets"     && <PresetLinesTab />}
        {raw && activeTab === "tiers"      && <TiersTab          raw={raw} />}
        {raw && activeTab === "commissions" && <CommissionsTab   raw={raw} onSaved={fetchRaw} />}
        {raw && activeTab === "notifications" && <NotificationsTab raw={raw} onSaved={fetchRaw} />}
        {       activeTab === "integrations" && <IntegrationsTab />}
      </div>
    </div>
  );
}
